import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { Logger } from 'src/services/core/logger/logger.service';
import { DishService } from 'src/services/data/dish/dish.service';
import { untilDestroyed } from 'src/services/core/until-destroyed/until-destroyed';
import { Response } from 'src/services/classes/models/response';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { AddOn, Meal } from 'src/services/classes/models/order-item';

const log = new Logger('Dish Details');

@Component({
  selector: 'app-dish-details',
  templateUrl: './dish-details.component.html',
  styleUrls: ['./dish-details.component.scss'],
})
export class DishDetailsComponent implements OnInit, OnDestroy {
  isLoading = false;
  extraMealDetails = null;
  cartForm: FormGroup;
  chosenQuantity = 1;
  selectedAddOnNames = [];
  isArray = Array.isArray;
  selectedAddOnGroups = [];

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: Meal,
    private bottomSheetRef: MatBottomSheetRef<DishDetailsComponent>,
    private dishService: DishService,
    private notifyService: NotificationsService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    log.debug('meal: ', this.data);
    this.createCartForm();

    if (this.data && this.data.id) {
      this.getMealDetails(Number(this.data.id));
    }
  }

  createCartForm() {
    this.cartForm = this.fb.group({
      addOns: [[]],
      quantity: [],
    });
  }

  closeBottomSheet() {
    this.bottomSheetRef.dismiss('closed');
  }

  notifyNoStock() {
    this.notifyService.publishMessages(
      'Add on is currently out of stock',
      'info',
      1
    );
  }

  parseCount(count: any) {
    return Number(count);
  }

  getMealDetails(mealId: number) {
    this.isLoading = true;
    const meal$ = this.dishService.getDishItem(mealId);
    meal$
      .pipe(
        finalize(() => (this.isLoading = false)),
        untilDestroyed(this)
      )
      .subscribe(
        (res: Response) => {
          if (res.status.toLowerCase() !== 'error') {
            if (res.data) {
              log.debug('res: ', res.data);
              this.extraMealDetails = res.data;
              if (this.data.quantityOrdered) {
                this.updateAddonsSelected();
              }
            }
          } else {
            this.notifyService.publishMessages(res.message, 'danger', 1);
          }
        },
        (error) => log.debug('err: ', error)
      );
  }

  updateAddonsSelected() {
    if (this.data && this.data.addOns && this.data.addOns.length > 0) {
      const addOnsFromAPI =
        this.extraMealDetails && this.extraMealDetails.addOns
          ? this.extraMealDetails.addOns
          : [];
      addOnsFromAPI.forEach((addOn: AddOn) => {
        Object.entries(addOn).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item: AddOn) => {
              this.data.addOns.forEach((addOn) => {
                if (item.name === addOn.name) {
                  this.selectAddOn(null, addOn, key, true);
                }
              });
            });
          }
        });
      });
    }
  }

  hyphenateCategory(category: string): string {
    if (category.includes(' ')) {
      let lowerCaseCategory = category.toLowerCase().trim();
      let hyphenatedCategory = lowerCaseCategory.replace(' ', '-');
      return hyphenatedCategory;
    } else {
      return category.toLowerCase();
    }
  }

  createLocationId(category: string): string {
    return this.hyphenateCategory(category);
  }

  changeMealQuantity(type: string) {
    if (type === 'increase') {
      if (this.data && this.data.quantityOrdered) {
        log.debug('++type: ', typeof this.data.quantityOrdered);
        this.data.quantityOrdered++;
      } else {
        this.chosenQuantity++;
      }
    } else {
      if (this.data && this.data.quantityOrdered) {
        log.debug('--type: ', typeof this.data.quantityOrdered);
        if (this.data.quantityOrdered > 0) {
          this.data.quantityOrdered--;
        }
      } else {
        if (this.chosenQuantity > 0) {
          this.chosenQuantity--;
        }
      }
    }

    log.debug('data: ', this.data);
  }

  selectAddOn(event: any, addOn: AddOn, addOnGroup: string, forEdit = false) {
    log.debug('e: ', event);
    const selectedValue = event !== null ? event.target.value : addOn.name;
    const { addOns } = this.cartForm.value;

    if (forEdit || (event && event.target.checked === true)) {
      if (Array.isArray(addOns)) {
        addOns.push(addOn);
        this.selectedAddOnNames.push(selectedValue);
        this.selectedAddOnGroups.push(addOnGroup);
      }
    } else {
      if (this.selectedAddOnNames.includes(selectedValue)) {
        const selectedValueIndex = this.selectedAddOnNames.findIndex(
          (name) => name === selectedValue
        );
        addOns.splice(selectedValueIndex, 1);
        this.selectedAddOnNames.splice(selectedValueIndex, 1);
      }

      if (this.selectedAddOnGroups.includes(addOnGroup)) {
        const selectedGroupIndex = this.selectedAddOnGroups.findIndex(
          (group) => group === addOnGroup
        );
        this.selectedAddOnGroups.splice(selectedGroupIndex, 1);
      }
    }

    log.debug('selectedNames: ', this.selectedAddOnNames);
    log.debug('form-addOns: ', addOns);
  }

  validateForRequiredAddOns() {
    const addOnsFromAPI =
      this.extraMealDetails && this.extraMealDetails.addOns
        ? this.extraMealDetails.addOns
        : [];
    const isRequiredButNotSelected = [];
    if (addOnsFromAPI && addOnsFromAPI.length > 0) {
      addOnsFromAPI.forEach((addOn: AddOn) => {
        if (addOn.isRequired === true) {
          Object.entries(addOn).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              for (const addOnItem of value) {
                if (Number(addOnItem.stockCount) > 0) {
                  this.selectedAddOnGroups.includes(key)
                    ? void 0
                    : isRequiredButNotSelected.push(key);
                }
              }
            }
          });
        }
      });
    }

    return [...new Set(isRequiredButNotSelected)].length;
  }

  showRequiredBadge(addOn: any) {
    let zeroStockCount = [];
    Object.entries(addOn).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        for (const addOnItem of value) {
          if (Number(addOnItem.stockCount) === 0) {
            zeroStockCount.push(addOnItem);
          }
        }
      }
    });

    return zeroStockCount.length > 0;
  }

  addItemToCart() {
    const isRequiredButNotSelectedLength = this.validateForRequiredAddOns();

    if (isRequiredButNotSelectedLength === 0) {
      if (this.data.quantityOrdered && this.chosenQuantity === 1) {
        this.cartForm.patchValue({
          quantity: this.data.quantityOrdered,
        });
      } else {
        this.cartForm.patchValue({
          quantity: this.chosenQuantity,
        });
      }

      const { name, price, id, imageUrl, takeawayPrice } = this.data;

      const cartPayload = {
        ...this.cartForm.value,
        name,
        price,
        id,
        imageUrl,
        takeawayPrice
      };

      console.log('Cart Payload:', cartPayload);
      log.debug('payload: ', cartPayload);
      this.bottomSheetRef.dismiss(cartPayload);
    } else {
      this.notifyService.publishMessages(
        'Kindly select an item from the required add-ons',
        'warning',
        1
      );
    }
  }

  isImageValid(control: string) {
    return /^(ftp|https|http)?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/.test(
      control
    );
  }

  ngOnDestroy() {}
}